import Konva from 'konva';
import { Element } from './element';
import { Equipment } from '../equipment';
import { Point } from '../../../core/models/point';
import { Extension, ExtensionType, RailsParams, RoundingParams } from './extension';
import { konvaSettings } from '../konvaSettings';
import { EquipmentElements } from './equipmentElements';
import { elementsGenerator } from './elementGenerator';
import { Figure } from '../figures/figure';
import { Rectangle } from '../figures/rectangle';
import { RectangleSliced } from '../figures/rectangle-sliced';
import { ShelfElement } from '../../../planogram/models/elements/shelfElement';
import { Utils } from '../../../core/services/utils.service';
import { UnitConverter } from '../../../core/utils/unitConverter';

const NOT_AVAILABLE_LABEL: string = 'N';
const getShelfTooltipValues = (element: ShelfElement, equipment: Equipment): Partial<ITooltipLabels> => {
	const partitionAndSidesWallOnShelves = element.getElementsOnShelf
			? element.getElementsOnShelf.filter(el => ['Partition', 'SidesWall'].includes(el.parent.type))
			: [],
		partitionsShapes = partitionAndSidesWallOnShelves.length
			? Utils.to1DArray(partitionAndSidesWallOnShelves.map(el => el.shapes))
				.filter(el => el.isPointOn(null, element.getBoundingY().min) || el.isPointOn(null, element.getBoundingY().max))
			: [],
		widthPartitionAndSideWall = partitionsShapes.length
			? partitionsShapes.map(el => el.width).reduce((a, b) => Number(a) + Number(b))
			: 0,
		layoutMeters: number = element.layoutMeters,
		freeSpaceInShelf = element.linearMeters - layoutMeters - widthPartitionAndSideWall,
		productsGroupLength = element.products.map(p => p.length),
		quantitySKU = productsGroupLength.length ? productsGroupLength.reduce((a, b) => Number(a) + Number(b)) : 0;
	return {
		SHELF: Utils.getElementIndex(element, equipment) || NOT_AVAILABLE_LABEL,
		LENGTH: UnitConverter.getConvertValue(element.Width, 'distance', 'inches', 3),
		LINEAR_METERS: UnitConverter.getConvertValue(layoutMeters, 'distance', 'feet', 3),
		FREE_SPACE_ON_THE_SHELF_M: UnitConverter.getConvertValue(freeSpaceInShelf, 'distance', 'inches', 3),
		QUANTITY_OF_LAID_OUT_SKU: quantitySKU
	};
};
const getBoxTooltipValues = (element: Element, equipment: Equipment): Partial<ITooltipLabels> => {
	const layoutMeters: number = element.layoutMeters,
		freeSpaceInBox = element.Width - layoutMeters - element.shapes[0].wallWidth * 2,
		productsGroupLength = element.products.map((p) => p.length),
		quantitySKU = productsGroupLength.length ? productsGroupLength.reduce((a, b) => Number(a) + Number(b)) : 0;

	return {
		'BOX': Utils.getElementIndex(element, equipment) || NOT_AVAILABLE_LABEL,
		'LENGTH': UnitConverter.getConvertValue(element.Width, 'distance', 'inches', 3),
		'LINEAR_METERS': UnitConverter.getConvertValue(layoutMeters, 'distance', 'feet', 3),
		'FREE_SPACE_IN_THE_BOX_M': UnitConverter.getConvertValue(freeSpaceInBox, 'distance', 'inches', 3),
		'QUANTITY_OF_LAID_OUT_SKU': quantitySKU
	};
};

export interface IEquipmentSideState {
	getPoint(element: Element, equipment: Equipment): Point;
}

export interface IElementDrawer {
	setBeginPoint(point: Point): IElementDrawer;
	setEquipment(equipment: Equipment): IElementDrawer;
	setOffset(offset: number): IElementDrawer;
	setScale(scale: number): IElementDrawer;
	setState(flip: boolean): IElementDrawer;
	draw(active: boolean): Konva.Group;
	drawTooltip(labels: ITooltipLabels, canvasElement: Konva.Group): Konva.Label;
}

export interface ITooltipLabels {
	SHELF: string | number;
	HANG_BAR: string | number;
	BOX: string | number;
	ONE_HOOK: string | number;
	LENGTH: string | number;
	DEPTH: string | number;
	LINEAR_METERS: string | number;
	FREE_SPACE_ON_THE_SHELF_M: string | number;
	FREE_SPACE_ON_THE_HANG_BAR_M: string | number;
	FREE_SPACE_IN_THE_BOX_M: string | number;
	FREE_SPACE_ON_THE_HOOK_M: string | number;
	QUANTITY_OF_LAID_OUT_SKU: string | number;
}

class ElementFrontState implements IEquipmentSideState {

	getPoint(element: Element, equipment: Equipment): Point {
		return {
			x: element.shapes[0].begin.x,
			y: equipment.Height - element.shapes[0].begin.y - element.shapes[0].height
		}
	}
}

class ElementBackState implements IEquipmentSideState {

	getPoint(element: Element, equipment: Equipment): Point {
		return {
			x: equipment.Width - element.shapes[0].begin.x - element.Width,
			y: equipment.Height - element.shapes[0].begin.y - element.shapes[0].height
		}
	}
}

class ElementDrawer implements IElementDrawer {

	protected _beginPoint: Point = { x: 0, y: 0 };
	protected _element: Element = null;
	protected _equipment: Equipment = null;
	protected _flip: boolean = false;
	protected _offset: number = 0;
	protected _scale: number = 0;
	protected _sideState: IEquipmentSideState;
	protected readonly _stripesImage: HTMLImageElement;

	constructor(element: Element) {
		this._element = element;
		this._stripesImage = new Image();
		this._stripesImage.src = '/static/img/icons/stripes.svg';
	}

	public setBeginPoint(point: Point): IElementDrawer {
		this._beginPoint = point;

		return this;
	}

	public setEquipment(equipment: Equipment): IElementDrawer {
		this._equipment = equipment;

		return this;
	}

	public setOffset(offset: number): IElementDrawer {
		this._offset = offset;

		return this;
	}

	public setScale(scale: number): IElementDrawer {
		this._scale = scale;

		return this;
	}

	public setState(flip: boolean = false): IElementDrawer {
		this._sideState = flip ? new ElementBackState() : new ElementFrontState();
		this._flip = flip;

		return this;
	}

	protected convertPointToCanvas(p: Point): Point {
		return {
			x: this._beginPoint.x + this._offset + p.x * this._scale,
			y: this._beginPoint.y + this._offset + p.y * this._scale
		};
	}

	public draw(active: boolean): Konva.Group {
		return new Konva.Group();
	};

	public drawTooltip(labels: ITooltipLabels, canvasElement: Konva.Group): Konva.Label {
		const values: Partial<ITooltipLabels> = this.getTooltipValues();

		if (!Object.keys(values).length) return null;

		const tooltip: Konva.Label = new Konva.Label({
			id: this._element.id,
			x: canvasElement.x() + canvasElement.width() / 2,
			y: canvasElement.y(),
			visible: false,
			opacity: 0.9
		});
		const tag: Konva.Tag = new Konva.Tag({
			fill: 'white',
			pointerDirection: 'down',
			pointerWidth: 10,
			pointerHeight: 10,
			lineJoin: 'round',
			shadowColor: 'black',
			shadowBlur: 10,
			shadowOpacity: 0.5
		});
		tooltip.add(tag);

		let tooltipText: string = '';
		Object.keys(values).forEach((key: string, idx: number) => {
			if (!idx) {
				tooltipText += `${labels[key]} ${values[key]}`;
			} else {
				tooltipText += `\n${labels[key]}: ${values[key]}`;
			}
		});

		const text = new Konva.Text({ fontSize: 12, text: tooltipText, padding: 5, fill: 'black' });
		tooltip.add(text);

		if (tooltip.y() - tooltip.height() - 10 < this._beginPoint.y + this._offset) {
			tooltip.y(canvasElement.y() + canvasElement.height());
			tag.pointerDirection('up');
		}

		if (tooltip.x() - tooltip.width() / 2 < 0) {
			tooltip.x(canvasElement.x() + canvasElement.width());
			tooltip.y(canvasElement.y() + canvasElement.height() / 2);
			tag.pointerDirection('left');
		}

		const totalWidth: number = Math.round(this._equipment.Width * this._scale + this._offset * 2 + this._beginPoint.x * 2);
		if (tooltip.x() + tooltip.width() / 2 > totalWidth) {
			tooltip.x(canvasElement.x());
			tooltip.y(canvasElement.y() + canvasElement.height() / 2);
			tag.pointerDirection('right');
		}

		return tooltip;
	}

	protected createLabel(text: string, fill: string = konvaSettings.fillWhiteColor): Konva.Group {
		const labelGroup: Konva.Group = new Konva.Group({
				name: 'label'
			}),
			labelText: Konva.Text = new Konva.Text({
				text: text,
				fontSize: 12,
				x: 3,
				y: 2
			}),
			labelRect: Konva.Rect = new Konva.Rect({
				width: labelText.getWidth() + 6,
				height: labelText.getHeight() + 4,
				stroke: 'black',
				strokeWidth: 1,
				fill: fill
			});

		labelGroup.add(labelRect);
		labelGroup.add(labelText);
		labelGroup.setAttrs({ width: labelRect.width(), height: labelRect.height() });

		return labelGroup;
	}

	protected drawHiddenLines(canvasElement: Konva.Group): Promise<Konva.Rect> {
		return new Promise((resolve) => {
			if (this._stripesImage.complete) {
				const stripesRect: Konva.Rect = new Konva.Rect({
					name: 'stripes',
					x: 1,
					y: 1,
					width: Math.max(canvasElement.width() - 2, 0),
					height: Math.max(canvasElement.height() - 2, 0),
					fillPatternImage: this._stripesImage,
					fillPatternRepeat: 'repeat',
					fillPatternScaleX: 20 / this._stripesImage.width,
					fillPatternScaleY: 20 / this._stripesImage.height
				});
				canvasElement.add(stripesRect);
				resolve(stripesRect);
			} else {
				this._stripesImage.onload = () => this.drawHiddenLines(canvasElement);
			}
		});
	}

	protected drawBackWallRails(active: boolean, canvasElement: Konva.Group): void {
		const sideWalls: Element = this._equipment.allElements
			.find((el: Element) => el.parent.is(EquipmentElements.SideWalls) && el.depend == this._element.depend);
		const sideWallsWidth: number = sideWalls
			? Utils.round(sideWalls.shapes[0].width + sideWalls.shapes[1].width)
			: 0;
		const rails: Extension<RailsParams> = this._element.getExtension<RailsParams>(ExtensionType.Rails);
		const railsWidth: number = Math.min(
			rails.params.railHolesWidth,
			Utils.round((this._element.Width - sideWallsWidth) / 2 - 0.02)
		);
		const connectedRails: boolean = rails.params.railHolesWidth >= Utils.round(this._element.Width / 2 - 0.02);

		if (!rails?.active) return;

		const leftRailGroup: Konva.Group = new Konva.Group({
				name: 'left-rail',
				x: 0.5,
				y: 0.5,
				width: (railsWidth + 0.02) * this._scale,
				height: canvasElement.height() - 1
			}),
			leftRail: Konva.Rect = new Konva.Rect({
				name: 'fill-active fill-light',
				x: 0,
				y: 0,
				width: leftRailGroup.width(),
				height: leftRailGroup.height(),
				fill: active ? konvaSettings.activeColor : konvaSettings.fillGreyColor,
				strokeWidth: 0
			}),
			rightRailGroup: Konva.Group = new Konva.Group({
				name: 'right-rail',
				x: canvasElement.width() - (railsWidth + 0.02) * this._scale - 0.5,
				y: 0.5,
				width: (railsWidth + 0.02) * this._scale,
				height: canvasElement.height() - 1
			}),
			rightRail: Konva.Rect = new Konva.Rect({
				name: 'fill-active fill-light',
				x: 0,
				y: 0,
				width: rightRailGroup.width(),
				height: rightRailGroup.height(),
				fill: active ? konvaSettings.activeColor : konvaSettings.fillGreyColor,
				strokeWidth: 0
			}),
			height: number = this._element.shapes[0].begin.y + this._element.Height;

		if (sideWalls) {
			leftRailGroup.offsetX(-sideWalls.shapes[0].width * this._scale + 1);
			rightRailGroup.offsetX(sideWalls.shapes[1].width * this._scale - 1);
		}

		leftRailGroup.add(leftRail);
		rightRailGroup.add(rightRail);

		elementsGenerator[EquipmentElements.BackWall].getAnchorPoints(this._element).forEach((point: number) => {
			leftRailGroup.add(new Konva.Rect({
				x: 0.01 * this._scale,
				y: (height - point - rails.params.railHolesHeight) * this._scale,
				width: (railsWidth + (connectedRails ? 0.01 : 0)) * this._scale,
				height: rails.params.railHolesHeight * this._scale,
				fill: konvaSettings.fillWhiteColor,
				strokeWidth: 0
			}));
			rightRailGroup.add(new Konva.Rect({
				x: (connectedRails ? 0 : 0.01) * this._scale,
				y: (height - point - rails.params.railHolesHeight) * this._scale,
				width: (railsWidth + (connectedRails ? 0.01 : 0)) * this._scale,
				height: rails.params.railHolesHeight * this._scale,
				fill: konvaSettings.fillWhiteColor,
				strokeWidth: 0
			}));
		});

		canvasElement.add(leftRailGroup, rightRailGroup);
	}

	protected getTooltipValues(): Partial<ITooltipLabels> {
		return {};
	}
}

export class RectangleBaseElementDrawer extends ElementDrawer {

	public draw(active: boolean): Konva.Group {
		const point: Point = this.convertPointToCanvas(this._sideState.getPoint(this._element, this._equipment));
		const elementGroup: Konva.Group = new Konva.Group({
			name: this._element.parent.type,
			x: point.x + 0.5,
			y: point.y + 0.5,
			width: this._element.shapes[0].width * this._scale - 1,
			height: this._element.shapes[0].height * this._scale - 1
		});
		let elementRect: Konva.Rect, labelText: string, labelFill: string;

		if (!this._element.isExtensionActive(ExtensionType.Availability)) {
			elementRect = new Konva.Rect({
				name: 'disabled stroke-dark stroke-active fill-hover-light',
				x: 0,
				y: 0,
				width: elementGroup.width(),
				height: elementGroup.height(),
				stroke: active ? konvaSettings.activeColor : konvaSettings.fillDarkColor,
				strokeWidth: 1,
				fill: konvaSettings.fillGreyColor
			});
			labelText = NOT_AVAILABLE_LABEL;
			labelFill = konvaSettings.fillGreyColor;
			elementGroup.add(elementRect);
			void this.drawHiddenLines(elementGroup);
		} else {
			elementRect = new Konva.Rect({
				name: 'dark active fill-hover-light',
				x: 0,
				y: 0,
				width: elementGroup.width(),
				height: elementGroup.height(),
				stroke: active ? konvaSettings.activeColor : konvaSettings.fillDarkColor,
				strokeWidth: 1,
				fill: active ? konvaSettings.activeColor : konvaSettings.fillDarkColor
			});
			labelText = String(1);
			labelFill = konvaSettings.fillWhiteColor;
			elementGroup.add(elementRect);
		}

		if (this._element.isExtensionActive(ExtensionType.Rounding) && elementGroup.height() > 0) {
			const rounding: Extension<RoundingParams> = this._element.getExtension<RoundingParams>(ExtensionType.Rounding);
			const radius: number = Math.max(Math.min(elementGroup.height(), elementGroup.width()) / 2, 0);

			if (radius) {
				if (this._flip) {
					elementRect.cornerRadius([
						rounding.params.rightSideRadius ? radius : 0,
						rounding.params.leftSideRadius ? radius : 0,
						rounding.params.leftSideRadius ? radius : 0,
						rounding.params.rightSideRadius ? radius : 0
					]);
				} else {
					elementRect.cornerRadius([
						rounding.params.leftSideRadius ? radius : 0,
						rounding.params.rightSideRadius ? radius : 0,
						rounding.params.rightSideRadius ? radius : 0,
						rounding.params.leftSideRadius ? radius : 0
					]);
				}
			}
		}

		const labelGroup: Konva.Group = this.createLabel(labelText, labelFill);
		labelGroup.setAttrs({ x: -labelGroup.width(), y: 0 });
		elementGroup.add(labelGroup);

		return elementGroup;
	}

	protected getTooltipValues(): Partial<ITooltipLabels> {
		return getShelfTooltipValues(this._element as ShelfElement, this._equipment);
	}
}

export class TrapezeBaseElementDrawer extends ElementDrawer {

	public draw(active: boolean): Konva.Group {
		const point: Point = this.convertPointToCanvas(this._sideState.getPoint(this._element, this._equipment));
		const width: number = Math.max(this._element.shapes[0].frontWidth, this._element.shapes[0].backWidth);
		const backOffset: number = (width - this._element.shapes[0].backWidth) / 2 * this._scale;
		const frontOffset: number = (width - this._element.shapes[0].frontWidth) / 2 * this._scale;
		const visibleSideNames: string = 'dark active fill-hover-light';
		const invisibleSideNames: string = 'fill-light stroke-dark stroke-active fill-hover-dark';
		const disabledSideNames: string = 'disabled stroke-dark stroke-active fill-hover-light';
		const elementGroup: Konva.Group = new Konva.Group({
			name: this._element.parent.type,
			x: point.x + 0.5,
			y: point.y + 0.5,
			width: width * this._scale - 1,
			height: this._element.shapes[0].height * this._scale - 1
		});
		const backGroup: Konva.Group = new Konva.Group({
			x: backOffset + 0.5,
			y: 0.5,
			width: elementGroup.width() - backOffset * 2 - 1,
			height: elementGroup.height() - 1,
		});
		const backRect: Konva.Rect = new Konva.Rect({
			x: 0,
			y: 0,
			width: backGroup.width(),
			height: backGroup.height(),
			stroke: active ? konvaSettings.activeColor : konvaSettings.fillDarkColor,
			strokeWidth: 1,
			fill: konvaSettings.fillGreyColor
		});
		backGroup.add(backRect);
		const frontGroup: Konva.Group = new Konva.Group({
			x: frontOffset + 0.5,
			y: 0.5,
			width: elementGroup.width() - frontOffset * 2 - 1,
			height: elementGroup.height() - 1,
		});
		const frontRect: Konva.Rect = new Konva.Rect({
			x: 0,
			y: 0,
			width: frontGroup.width(),
			height: frontGroup.height(),
			stroke: active ? konvaSettings.activeColor : konvaSettings.fillDarkColor,
			strokeWidth: 1,
			fill: konvaSettings.fillDarkColor
		});
		frontGroup.add(frontRect);

		let labelText: string, labelFill: string, frontNames: string, frontColor: string;

		if (!this._element.isExtensionActive(ExtensionType.Availability)) {
			labelText = NOT_AVAILABLE_LABEL;
			labelFill = konvaSettings.fillGreyColor;
			frontNames = disabledSideNames;
			frontColor = konvaSettings.fillGreyColor;
			void this.drawHiddenLines(this._flip ? backGroup : frontGroup);
		} else {
			labelText = String(1);
			labelFill = konvaSettings.fillWhiteColor;
			frontNames = visibleSideNames;
			frontColor = konvaSettings.fillDarkColor;
		}

		if (this._flip) {
			frontRect.name(invisibleSideNames);
			frontRect.fill(konvaSettings.fillGreyColor);
			backRect.name(frontNames);
			backRect.fill(active && this._element.isExtensionActive(ExtensionType.Availability) ? konvaSettings.activeColor : frontColor);
			elementGroup.add(frontGroup, backGroup);
		} else {
			backRect.name(invisibleSideNames);
			backRect.fill(konvaSettings.fillGreyColor);
			frontRect.name(frontNames);
			frontRect.fill(active && this._element.isExtensionActive(ExtensionType.Availability) ? konvaSettings.activeColor : frontColor);
			elementGroup.add(backGroup, frontGroup);
		}

		const labelGroup: Konva.Group = this.createLabel(labelText, labelFill);
		labelGroup.setAttrs({ x: -labelGroup.width(), y: 0 });
		elementGroup.add(labelGroup);

		return elementGroup;
	}

	protected getTooltipValues() {
		return getShelfTooltipValues(this._element as ShelfElement, this._equipment);
	}
}

export class BackWallElementDrawer extends ElementDrawer {

	public draw(active: boolean): Konva.Group {
		const point: Point = this.convertPointToCanvas(this._sideState.getPoint(this._element, this._equipment));
		const elementGroup: Konva.Group = new Konva.Group({
			name: this._element.parent.type,
			x: point.x + 0.5,
			y: point.y + 0.5,
			width: this._element.shapes[0].width * this._scale - 1,
			height: this._element.shapes[0].height * this._scale - 1
		});

		elementGroup.add(new Konva.Rect({
			name: 'stroke-dark stroke-active',
			x: 0,
			y: 0,
			width: elementGroup.width(),
			height: elementGroup.height(),
			stroke: active ? konvaSettings.activeColor : konvaSettings.fillDarkColor,
			strokeWidth: 1,
			fill: konvaSettings.fillWhiteColor
		}));

		if (this._element.isExtensionActive(ExtensionType.Rails) && !this._flip) {
			this.drawBackWallRails(active, elementGroup);
		}

		return elementGroup;
	}
}

export class CentralPillarElementDrawer extends ElementDrawer {

	public draw(active: boolean): Konva.Group {
		const point: Point = this.convertPointToCanvas(this._sideState.getPoint(this._element, this._equipment));
		const elementGroup: Konva.Group = new Konva.Group({
			name: this._element.parent.type,
			x: point.x + 0.5,
			y: point.y + 0.5,
			width: this._element.shapes[0].width * this._scale - 1,
			height: this._element.shapes[0].height * this._scale - 1
		});

		elementGroup.add(new Konva.Rect({
			name: 'stroke-dark stroke-active',
			x: 0,
			y: 0,
			width: elementGroup.width(),
			height: elementGroup.height(),
			stroke: active ? konvaSettings.activeColor : konvaSettings.fillDarkColor,
			strokeWidth: 1,
			fill: konvaSettings.fillWhiteColor
		}));

		if (this._element.isExtensionActive(ExtensionType.Rails)) {
			this.drawBackWallRails(active, elementGroup);
		}

		return elementGroup;
	}
}

export class HookWallElementDrawer extends ElementDrawer {

	public draw(active: boolean): Konva.Group {
		const point: Point = this.convertPointToCanvas(this._sideState.getPoint(this._element, this._equipment));
		const elementGroup: Konva.Group = new Konva.Group({
			name: this._element.parent.type,
			x: point.x + 0.5,
			y: point.y + 0.5,
			width: this._element.shapes[0].width * this._scale - 1,
			height: this._element.shapes[0].height * this._scale - 1
		});

		elementGroup.add(new Konva.Rect({
			name: 'stroke-dark stroke-active',
			x: 0,
			y: 0,
			width: elementGroup.width(),
			height: elementGroup.height(),
			stroke: active ? konvaSettings.activeColor : konvaSettings.fillDarkColor,
			strokeWidth: 1,
			fill: konvaSettings.fillWhiteColor
		}));

		return elementGroup;
	}
}

export class PerforatedWallElementDrawer extends ElementDrawer {

	public draw(active: boolean): Konva.Group {
		const point: Point = this.convertPointToCanvas(this._sideState.getPoint(this._element, this._equipment));
		const elementGroup: Konva.Group = new Konva.Group({
			name: this._element.parent.type,
			x: point.x + 0.5,
			y: point.y + 0.5,
			width: this._element.shapes[0].width * this._scale - 1,
			height: this._element.shapes[0].height * this._scale - 1
		});

		elementGroup.add(new Konva.Rect({
			name: 'stroke-dark stroke-active',
			x: 0,
			y: 0,
			width: elementGroup.width(),
			height: elementGroup.height(),
			stroke: active ? konvaSettings.activeColor : konvaSettings.fillDarkColor,
			strokeWidth: 1,
			fill: konvaSettings.fillWhiteColor
		}));

		this._equipment.allElements.forEach((el: Element) => {
			if (el.depend == this._element) {
				elementGroup.add(new Konva.Rect({
					name: 'stroke-light stroke-active',
					x: (el.shapes[0].begin.x - el.depend.shapes[0].begin.x) * this._scale,
					y: (el.depend.shapes[0].height - el.shapes[0].begin.y + el.depend.shapes[0].begin.y - el.shapes[0].height) * this._scale,
					width: 0.01 * this._scale,
					height: 0.01 * this._scale,
					stroke: active ? konvaSettings.activeColor : konvaSettings.fillGreyColor,
					strokeWidth: 1,
					fill: konvaSettings.fillWhiteColor
				}));
			}
		});

		return elementGroup;
	}
}

class SideWallsFrontState implements IEquipmentSideState {

	getPoint(element: Element, equipment: Equipment): Point {
		const y: number = Math.max(element.shapes[0].begin.y, element.shapes[1].begin.y);
		const height: number = Math.max(element.shapes[0].height, element.shapes[1].height);

		return {
			x: element.depend.getBoundingX().min,
			y: equipment.Height - y - height
		}
	}
}

class SideWallsBackState implements IEquipmentSideState {

	getPoint(element: Element, equipment: Equipment): Point {
		const y: number = Math.max(element.shapes[0].begin.y, element.shapes[1].begin.y);
		const height: number = Math.max(element.shapes[0].height, element.shapes[1].height);

		return {
			x: equipment.Width - element.depend.getBoundingX().max,
			y: equipment.Height - y - height
		}
	}
}

export class SideWallsElementDrawer extends ElementDrawer {

	public setState(flip: boolean = false): IElementDrawer {
		this._sideState = flip ? new SideWallsBackState() : new SideWallsFrontState();

		return this;
	}

	public draw(active: boolean): Konva.Group {
		const point: Point = this.convertPointToCanvas(this._sideState.getPoint(this._element, this._equipment));
		const elementGroup: Konva.Group = new Konva.Group({
			name: this._element.parent.type,
			x: point.x,
			y: point.y,
			width: this._element.depend.Width * this._scale,
			height: this._element.Height * this._scale
		});

		let startLeftWall: number = 0,
			endLeftWall: number = this._element.shapes[0].width,
			innerWalls: boolean = this._element.depend.shapes[0].frontWidth < this._element.depend.shapes[0].backWidth;

		if (this._element.depend.parent.is(EquipmentElements.BaseTrapeze)) {
			const baseShape: Figure = this._element.depend.shapes[0];
			endLeftWall = Math.abs(baseShape.frontWidth - baseShape.backWidth) / 2 + this._element.shapes[0].width;
		}

		const leftWallGroup: Konva.Group = new Konva.Group({
			x: startLeftWall * this._scale,
			y: (this._element.Height - this._element.shapes[0].height) * this._scale,
			width: (endLeftWall - startLeftWall) * this._scale,
			height: this._element.shapes[0].height * this._scale
		});

		leftWallGroup.add(new Konva.Rect({
			name: 'stroke-dark stroke-active',
			x: 0.5,
			y: 0.5,
			width: leftWallGroup.width() - 1,
			height: leftWallGroup.height() - 1,
			stroke: active ? konvaSettings.activeColor : konvaSettings.fillDarkColor,
			strokeWidth: 1,
			fill: konvaSettings.fillGreyColor
		}));

		leftWallGroup.add(new Konva.Rect({
			name: 'fill-dark fill-active',
			x: innerWalls ? leftWallGroup.width() - this._element.shapes[0].width * this._scale : 0,
			y: 0,
			width: this._element.shapes[0].width * this._scale,
			height: leftWallGroup.height(),
			strokeWidth: 0,
			fill: active ? konvaSettings.activeColor : konvaSettings.fillDarkColor
		}));

		elementGroup.add(leftWallGroup);

		let startRightWall: number = this._element.depend.Width - this._element.shapes[1].width,
			endRightWall: number = this._element.depend.Width;

		if (this._element.depend.parent.is(EquipmentElements.BaseTrapeze)) {
			const baseShape: Figure = this._element.depend.shapes[0];
			startRightWall = this._element.depend.Width - Math.abs(baseShape.frontWidth - baseShape.backWidth) / 2 - this._element.shapes[1].width;
		}

		const rightWallGroup: Konva.Group = new Konva.Group({
			x: startRightWall * this._scale,
			y: (this._element.Height - this._element.shapes[1].height) * this._scale,
			width: (endRightWall - startRightWall) * this._scale,
			height: this._element.shapes[1].height * this._scale
		});

		rightWallGroup.add(new Konva.Rect({
			name: 'stroke-dark stroke-active',
			x: 0.5,
			y: 0.5,
			width: rightWallGroup.width() - 1,
			height: rightWallGroup.height() - 1,
			stroke: active ? konvaSettings.activeColor : konvaSettings.fillDarkColor,
			strokeWidth: 1,
			fill: konvaSettings.fillGreyColor
		}));

		rightWallGroup.add(new Konva.Rect({
			name: 'fill-dark fill-active',
			x: innerWalls ? 0 : rightWallGroup.width() - this._element.shapes[1].width * this._scale,
			y: 0,
			width: this._element.shapes[1].width * this._scale,
			height: rightWallGroup.height(),
			strokeWidth: 0,
			fill: active ? konvaSettings.activeColor : konvaSettings.fillDarkColor
		}));

		elementGroup.add(rightWallGroup);

		return elementGroup;
	}
}

export class ShowCaseElementDrawer extends ElementDrawer {

	public draw(active: boolean): Konva.Group {
		const point: Point = this.convertPointToCanvas(this._sideState.getPoint(this._element, this._equipment));
		const elementGroup: Konva.Group = new Konva.Group({
			name: this._element.parent.type,
			x: point.x + 0.5,
			y: point.y + 0.5,
			width: this._element.Width * this._scale - 1,
			height: this._element.Height * this._scale - 1
		});

		elementGroup.add(new Konva.Rect({
			name: 'stroke-dark stroke-active',
			x: 0,
			y: 0,
			width: elementGroup.width(),
			height: elementGroup.height(),
			stroke: active ? konvaSettings.activeColor : konvaSettings.fillDarkColor,
			strokeWidth: 1,
			fill: 'transparent'
		}));

		return elementGroup;
	}
}

export class ShelfElementDrawer extends ElementDrawer {

	public draw(active: boolean): Konva.Group {
		const point: Point = this.convertPointToCanvas(this._sideState.getPoint(this._element, this._equipment));
		const elementGroup: Konva.Group = new Konva.Group({
			name: this._element.parent.type,
			x: point.x + 0.5,
			y: point.y + 0.5,
			width: this._element.Width * this._scale - 1,
			height: this._element.Height * this._scale - 1
		});
		let elementRect: Konva.Rect, labelText: string, labelFill: string;

		if (!this._element.isExtensionActive(ExtensionType.Availability)) {
			elementRect = new Konva.Rect({
				name: 'disabled stroke-dark stroke-active fill-hover-light',
				x: 0,
				y: 0,
				width: elementGroup.width(),
				height: elementGroup.height(),
				stroke: active ? konvaSettings.activeColor : konvaSettings.fillDarkColor,
				strokeWidth: 1,
				fill: konvaSettings.fillGreyColor
			});
			labelText = NOT_AVAILABLE_LABEL;
			labelFill = konvaSettings.fillGreyColor;
			elementGroup.add(elementRect);
			void this.drawHiddenLines(elementGroup);
		} else {
			elementRect = new Konva.Rect({
				name: 'dark active fill-hover-light',
				x: 0,
				y: 0,
				width: elementGroup.width(),
				height: elementGroup.height(),
				stroke: active ? konvaSettings.activeColor : konvaSettings.fillDarkColor,
				strokeWidth: 1,
				fill: active ? konvaSettings.activeColor : konvaSettings.fillDarkColor
			});
			labelText = String(Utils.getElementIndex(this._element, this._equipment));
			labelFill = konvaSettings.fillWhiteColor;
			elementGroup.add(elementRect);
		}

		if (this._element.isExtensionActive(ExtensionType.Rounding) && elementGroup.height() > 0) {
			const rounding: Extension<RoundingParams> = this._element.getExtension<RoundingParams>(ExtensionType.Rounding);
			const radius: number = Math.max(Math.min(elementGroup.height(), elementGroup.width()) / 2, 0);

			if (radius) {
				if (this._flip) {
					elementRect.cornerRadius([
						rounding.params.rightSideRadius ? radius : 0,
						rounding.params.leftSideRadius ? radius : 0,
						rounding.params.leftSideRadius ? radius : 0,
						rounding.params.rightSideRadius ? radius : 0
					]);
				} else {
					elementRect.cornerRadius([
						rounding.params.leftSideRadius ? radius : 0,
						rounding.params.rightSideRadius ? radius : 0,
						rounding.params.rightSideRadius ? radius : 0,
						rounding.params.leftSideRadius ? radius : 0
					]);
				}
			}
		}

		const labelGroup: Konva.Group = this.createLabel(labelText, labelFill);
		labelGroup.setAttrs({ x: -labelGroup.width(), y: 0 });
		elementGroup.add(labelGroup);

		return elementGroup;
	}

	protected getTooltipValues(): Partial<ITooltipLabels> {
		return getShelfTooltipValues(this._element as ShelfElement, this._equipment);
	}
}

export class AngleShelfElementDrawer extends ElementDrawer {

	public draw(active: boolean): Konva.Group {
		const point: Point = this.convertPointToCanvas(this._sideState.getPoint(this._element, this._equipment));
		const elementGroup: Konva.Group = new Konva.Group({
			name: this._element.parent.type,
			x: point.x + 0.5,
			y: point.y + 0.5,
			width: this._element.Width * this._scale - 1,
			height: this._element.Height * this._scale - 1
		});

		let labelText: string, labelFill: string;

		if (!this._element.isExtensionActive(ExtensionType.Availability)) {
			const angleGroup: Konva.Group = new Konva.Group({
				x: 0,
				y: 0,
				width: elementGroup.width(),
				height: elementGroup.height(),
			});
			angleGroup.add(new Konva.Rect({
				name: 'disabled stroke-dark stroke-active fill-hover-dark',
				x: 0,
				y: 0,
				width: angleGroup.width(),
				height: angleGroup.height(),
				stroke: active ? konvaSettings.activeColor : konvaSettings.fillDarkColor,
				strokeWidth: 1,
				fill: konvaSettings.fillGreyColor
			}));
			elementGroup.add(angleGroup);

			const frontGroup: Konva.Group = new Konva.Group({
				x: 0,
				y: elementGroup.height() - this._element.shapes[0].height * this._scale,
				width: elementGroup.width(),
				height: this._element.shapes[0].height * this._scale,
			});
			frontGroup.add(new Konva.Rect({
				name: 'disabled stroke-dark stroke-active fill-hover-light',
				x: 0,
				y: 0,
				width: frontGroup.width(),
				height: frontGroup.height(),
				stroke: active ? konvaSettings.activeColor : konvaSettings.fillDarkColor,
				strokeWidth: 1,
				fill: konvaSettings.fillGreyColor
			}));
			elementGroup.add(frontGroup);

			labelText = NOT_AVAILABLE_LABEL;
			labelFill = konvaSettings.fillGreyColor;
			void this.drawHiddenLines(angleGroup);
			void this.drawHiddenLines(frontGroup);
		} else {
			elementGroup.add(new Konva.Rect({
				name: 'fill-light stroke-dark stroke-active fill-hover-dark',
				x: 0,
				y: 0,
				width: elementGroup.width(),
				height: elementGroup.height(),
				stroke: active ? konvaSettings.activeColor : konvaSettings.fillDarkColor,
				strokeWidth: 1,
				fill: konvaSettings.fillGreyColor
			}));
			elementGroup.add(new Konva.Rect({
				name: 'fill-dark fill-active fill-hover-light',
				x: 0,
				y: elementGroup.height() - this._element.shapes[0].height * this._scale,
				width: elementGroup.width(),
				height: this._element.shapes[0].height * this._scale,
				strokeWidth: 0,
				fill: active ? konvaSettings.activeColor : konvaSettings.fillDarkColor
			}));
			labelText = String(Utils.getElementIndex(this._element, this._equipment));
			labelFill = konvaSettings.fillWhiteColor;
		}

		const labelGroup: Konva.Group = this.createLabel(labelText, labelFill);
		labelGroup.setAttrs({
			x: -labelGroup.width(),
			y: elementGroup.height() - this._element.shapes[0].height * this._scale
		});
		elementGroup.add(labelGroup);

		return elementGroup;
	}

	protected getTooltipValues(): Partial<ITooltipLabels> {
		return getShelfTooltipValues(this._element as ShelfElement, this._equipment);
	}
}

class HangBarFrontState implements IEquipmentSideState {

	getPoint(element: Element, equipment: Equipment): Point {
		return {
			x: element.getBoundingX().min,
			y: equipment.Height - element.getBoundingY().max
		}
	}
}

class HangBarBackState implements IEquipmentSideState {

	getPoint(element: Element, equipment: Equipment): Point {
		return {
			x: equipment.Width - element.getBoundingX().max,
			y: equipment.Height - element.getBoundingY().max
		}
	}
}

export class HangBarElementDrawer extends ElementDrawer {

	public setState(flip: boolean = false): IElementDrawer {
		this._sideState = flip ? new HangBarBackState() : new HangBarFrontState();

		return this;
	}

	public draw(active: boolean): Konva.Group {
		const point: Point = this.convertPointToCanvas(this._sideState.getPoint(this._element, this._equipment));
		const elementGroup: Konva.Group = new Konva.Group({
			name: this._element.parent.type,
			x: point.x + 0.5,
			y: point.y + 0.5,
			width: this._element.Width * this._scale - 1,
			height: this._element.Height * this._scale - 1
		});

		elementGroup.add(new Konva.Rect({
			name: 'fill-light stroke-dark stroke-active fill-hover-dark',
			x: 0,
			y: 0,
			width: elementGroup.width(),
			height: elementGroup.height(),
			stroke: active ? konvaSettings.activeColor : konvaSettings.fillDarkColor,
			strokeWidth: 1,
			fill: konvaSettings.fillGreyColor,
			cornerRadius: Math.max(elementGroup.height() / 2, 0)
		}));

		let labelText: string, labelFill: string;

		if (!this._element.isExtensionActive(ExtensionType.Availability)) {
			labelText = NOT_AVAILABLE_LABEL;
			labelFill = konvaSettings.fillGreyColor;
			this.drawHiddenLines(elementGroup).then((stripes: Konva.Rect) => {
				stripes.setAttr('cornerRadius', Math.max(elementGroup.height() / 2, 0));
			});
		} else {
			labelText = String(Utils.getElementIndex(this._element, this._equipment));
			labelFill = konvaSettings.fillWhiteColor;
		}

		const labelGroup: Konva.Group = this.createLabel(labelText, labelFill);
		labelGroup.setAttrs({
			x: -labelGroup.width(),
			y: elementGroup.height() / 2
		});
		elementGroup.add(labelGroup);

		return elementGroup;
	}

	protected getTooltipValues(): Partial<ITooltipLabels> {
		const layoutMeters: number = this._element.layoutMeters,
			freeSpace: number = Utils.round(this._element.Width - layoutMeters),
			productsGroupLength = this._element.products.map(p => p.length),
			quantitySKU = productsGroupLength.length ? productsGroupLength.reduce((a, b) => Number(a) + Number(b)) : 0;

		return {
			HANG_BAR: Utils.getElementIndex(this._element, this._equipment) || NOT_AVAILABLE_LABEL,
			LENGTH: UnitConverter.getConvertValue(this._element.Width, 'distance', 'inches', 3),
			LINEAR_METERS: UnitConverter.getConvertValue(layoutMeters, 'distance', 'feet', 3),
			FREE_SPACE_ON_THE_HANG_BAR_M: UnitConverter.getConvertValue(freeSpace, 'distance', 'inches', 3),
			QUANTITY_OF_LAID_OUT_SKU: quantitySKU
		};
	}
}

class BoxFrontState implements IEquipmentSideState {

	getPoint(element: Element, equipment: Equipment): Point {
		let end: Point = {
			x: Utils.round(element.shapes[0].begin.x + element.shapes[0].width),
			y: Utils.round(element.shapes[0].begin.y + element.shapes[0].height),
			z: element.shapes[0].begin.z
		};

		if (element.depend.parent.is(EquipmentElements.AngleShelf)) {
			end = (element.shapes[0] as Rectangle).rotatePointByFrontAngle(end);
		}

		return {
			x: element.shapes[0].begin.x,
			y: equipment.Height - end.y
		}
	}
}

class BoxBackState implements IEquipmentSideState {

	getPoint(element: Element, equipment: Equipment): Point {
		let end: Point = {
			x: Utils.round(element.shapes[0].begin.x + element.shapes[0].width),
			y: Utils.round(element.shapes[0].begin.y + element.shapes[0].height),
			z: element.shapes[0].begin.z
		};

		if (element.depend.parent.is(EquipmentElements.AngleShelf)) {
			end = (element.shapes[0] as Rectangle).rotatePointByFrontAngle(end);
		}

		return {
			x: equipment.Width - end.x,
			y: equipment.Height - end.y
		}
	}
}

export class BoxElementDrawer extends ElementDrawer {

	public setState(flip: boolean = false): IElementDrawer {
		this._sideState = flip ? new BoxBackState() : new BoxFrontState();

		return this;
	}

	public draw(active: boolean): Konva.Group {
		const point: Point = this.convertPointToCanvas(this._sideState.getPoint(this._element, this._equipment));
		let begin: Point = {
				...this._element.shapes[0].begin,
				z: Utils.round(this._element.shapes[0].begin.z + this._element.shapes[0].depth)
			},
			end: Point = {
				x: Utils.round(this._element.shapes[0].begin.x + this._element.shapes[0].width),
				y: Utils.round(this._element.shapes[0].begin.y + this._element.shapes[0].height),
				z: this._element.shapes[0].begin.z
			};

		if (this._element.depend.parent.is(EquipmentElements.AngleShelf)) {
			begin = (this._element.shapes[0] as Rectangle).rotatePointByFrontAngle(begin);
			end = (this._element.shapes[0] as Rectangle).rotatePointByFrontAngle(end);
		}

		const elementGroup: Konva.Group = new Konva.Group({
			name: this._element.parent.type,
			x: point.x + 0.5,
			y: point.y + 0.5,
			width: (end.x - begin.x) * this._scale - 1,
			height: (end.y - begin.y) * this._scale - 1
		});
		let labelText: string, labelFill: string;

		if (!this._element.isExtensionActive(ExtensionType.Availability)) {
			labelText = NOT_AVAILABLE_LABEL;
			labelFill = konvaSettings.fillGreyColor;
		} else {
			labelText = String(Utils.getElementIndex(this._element, this._equipment));
			labelFill = konvaSettings.fillWhiteColor;
		}

		const labelGroup: Konva.Group = this.createLabel(labelText, labelFill);

		elementGroup.add(new Konva.Rect({
			name: 'fill-dark fill-active',
			x: 0,
			y: 0,
			width: this._element.shapes[0].wallWidth * this._scale,
			height: elementGroup.height(),
			strokeWidth: 0,
			fill: active ? konvaSettings.activeColor : konvaSettings.fillDarkColor
		}));

		elementGroup.add(new Konva.Rect({
			name: 'fill-dark fill-active',
			x: elementGroup.width() - this._element.shapes[0].wallWidth * this._scale,
			y: 0,
			width: this._element.shapes[0].wallWidth * this._scale,
			height: elementGroup.height(),
			strokeWidth: 0,
			fill: active ? konvaSettings.activeColor : konvaSettings.fillDarkColor
		}));

		elementGroup.add(new Konva.Rect({
			name: 'front-wall stroke-dark stroke-active',
			x: 0,
			y: 0,
			width: elementGroup.width(),
			height: elementGroup.height(),
			stroke: active ? konvaSettings.activeColor : konvaSettings.fillDarkColor,
			strokeWidth: 1,
			fill: konvaSettings.fillWhiteColor
		}));

		labelGroup.setAttrs({
			x: elementGroup.width() - labelGroup.width() - 1,
			y: elementGroup.height() - labelGroup.height() - 1
		});

		elementGroup.add(labelGroup);

		return elementGroup;
	}

	protected getTooltipValues(): Partial<ITooltipLabels> {
		return getBoxTooltipValues(this._element, this._equipment);
	}
}

class SlicedBoxFrontState implements IEquipmentSideState {

	getPoint(element: Element, equipment: Equipment): Point {
		let frontPoint: Point = {
				x: element.shapes[0].begin.x,
				y: Utils.round(element.shapes[0].begin.y + element.shapes[0].frontHeight),
				z: Utils.round(element.shapes[0].begin.z + element.shapes[0].depth)
			},
			backPoint: Point = {
				x: element.shapes[0].begin.x,
				y: Utils.round(element.shapes[0].begin.y + element.shapes[0].backHeight),
				z: element.shapes[0].begin.z
			};

		if (element.depend.parent.is(EquipmentElements.AngleShelf)) {
			frontPoint = (element.shapes[0] as RectangleSliced).rotatePointByFrontAngle(frontPoint);
			backPoint = (element.shapes[0] as RectangleSliced).rotatePointByFrontAngle(backPoint);
		}

		return {
			x: element.shapes[0].begin.x,
			y: equipment.Height - Math.max(frontPoint.y, backPoint.y)
		}
	}
}

class SlicedBoxBackState implements IEquipmentSideState {

	getPoint(element: Element, equipment: Equipment): Point {
		let frontPoint: Point = {
				x: Utils.round(element.shapes[0].begin.x + element.shapes[0].width),
				y: Utils.round(element.shapes[0].begin.y + element.shapes[0].frontHeight),
				z: Utils.round(element.shapes[0].begin.z + element.shapes[0].depth)
			},
			backPoint: Point = {
				x: Utils.round(element.shapes[0].begin.x + element.shapes[0].width),
				y: Utils.round(element.shapes[0].begin.y + element.shapes[0].backHeight),
				z: element.shapes[0].begin.z
			};

		if (element.depend.parent.is(EquipmentElements.AngleShelf)) {
			frontPoint = (element.shapes[0] as RectangleSliced).rotatePointByFrontAngle(frontPoint);
			backPoint = (element.shapes[0] as RectangleSliced).rotatePointByFrontAngle(backPoint);
		}

		return {
			x: equipment. Width - frontPoint.x,
			y: equipment.Height - Math.max(frontPoint.y, backPoint.y)
		}
	}
}

export class SlicedBoxElementDrawer extends ElementDrawer {

	public setState(flip: boolean = false): IElementDrawer {
		this._sideState = flip ? new SlicedBoxBackState() : new SlicedBoxFrontState();

		return this;
	}

	public draw(active: boolean): Konva.Group {
		const point: Point = this.convertPointToCanvas(this._sideState.getPoint(this._element, this._equipment));
		let begin: Point = {
				...this._element.shapes[0].begin,
				z: Utils.round(this._element.shapes[0].begin.z + this._element.shapes[0].depth)
			},
			topBegin: Point = {
				x: this._element.shapes[0].begin.x,
				y: Utils.round(this._element.shapes[0].begin.y + this._element.shapes[0].frontHeight),
				z: Utils.round(this._element.shapes[0].begin.z + this._element.shapes[0].depth)
			},
			end: Point = {
				x: Utils.round(this._element.shapes[0].begin.x + this._element.shapes[0].width),
				y: Utils.round(this._element.shapes[0].begin.y + this._element.shapes[0].backHeight),
				z: this._element.shapes[0].begin.z
			};

		if (this._element.depend.parent.is(EquipmentElements.AngleShelf)) {
			begin = (this._element.shapes[0] as RectangleSliced).rotatePointByFrontAngle(begin);
			topBegin = (this._element.shapes[0] as RectangleSliced).rotatePointByFrontAngle(topBegin);
			end = (this._element.shapes[0] as RectangleSliced).rotatePointByFrontAngle(end);
		}

		const elementGroup: Konva.Group = new Konva.Group({
			name: this._element.parent.type,
			x: point.x,
			y: point.y,
			width: (end.x - begin.x) * this._scale,
			height: (Math.max(topBegin.y, end.y) - begin.y) * this._scale
		});
		let labelText: string, labelFill: string;

		if (!this._element.isExtensionActive(ExtensionType.Availability)) {
			labelText = NOT_AVAILABLE_LABEL;
			labelFill = konvaSettings.fillGreyColor;
		} else {
			labelText = String(Utils.getElementIndex(this._element, this._equipment));
			labelFill = konvaSettings.fillWhiteColor;
		}

		const labelGroup: Konva.Group = this.createLabel(labelText, labelFill);

		elementGroup.add(new Konva.Rect({
			name: 'back-wall stroke-dark stroke-active',
			x: 0.5,
			y: 0.5,
			width: elementGroup.width() - 1,
			height: elementGroup.height() - 1,
			stroke: active ? konvaSettings.activeColor : konvaSettings.fillDarkColor,
			strokeWidth: 1,
			fill: konvaSettings.fillGreyColor
		}));

		elementGroup.add(new Konva.Rect({
			name: 'fill-dark fill-active',
			x: 0,
			y: 0,
			width: this._element.shapes[0].wallWidth * this._scale,
			height: elementGroup.height(),
			strokeWidth: 0,
			fill: active ? konvaSettings.activeColor : konvaSettings.fillDarkColor
		}));

		elementGroup.add(new Konva.Rect({
			name: 'fill-dark fill-active',
			x: elementGroup.width() - this._element.shapes[0].wallWidth * this._scale,
			y: 0,
			width: this._element.shapes[0].wallWidth * this._scale,
			height: elementGroup.height(),
			strokeWidth: 0,
			fill: active ? konvaSettings.activeColor : konvaSettings.fillDarkColor
		}));

		elementGroup.add(new Konva.Rect({
			name: 'front-wall stroke-dark stroke-active',
			x: 0.5,
			y: 0.5 + (Math.max(topBegin.y, end.y) - topBegin.y) * this._scale,
			width: elementGroup.width() - 1,
			height: (topBegin.y - begin.y) * this._scale - 1,
			stroke: active ? konvaSettings.activeColor : konvaSettings.fillDarkColor,
			strokeWidth: 1,
			fill: konvaSettings.fillWhiteColor
		}));

		labelGroup.setAttrs({
			x: elementGroup.width() - labelGroup.width() - 1,
			y: elementGroup.height() - labelGroup.height() - 1
		});

		elementGroup.add(labelGroup);

		return elementGroup;
	}

	protected getTooltipValues(): Partial<ITooltipLabels> {
		return getBoxTooltipValues(this._element, this._equipment);
	}
}

export class HookElementDrawer extends ElementDrawer {

	public draw(active: boolean): Konva.Group {
		const point: Point = this.convertPointToCanvas(this._sideState.getPoint(this._element, this._equipment));
		const elementGroup: Konva.Group = new Konva.Group({
			name: this._element.parent.type,
			x: point.x + 0.5,
			y: point.y + 0.5,
			width: this._element.Width * this._scale - 1,
			height: this._element.Height * this._scale - 1
		});

		/*elementGroup.add(new Konva.Rect({
			name: 'clickable-offset',
			x: -elementGroup.width(),
			y: -elementGroup.height(),
			width: elementGroup.width() * 3,
			height: elementGroup.height() * 3,
			strokeWidth: 0,
			fill: 'transparent'
		}));*/

		if (!this._element.isExtensionActive(ExtensionType.Availability)) {
			elementGroup.add(new Konva.Rect({
				name: 'disabled stroke-dark stroke-active fill-hover-light',
				x: 0,
				y: 0,
				width: elementGroup.width(),
				height: elementGroup.height(),
				stroke: active ? konvaSettings.activeColor : konvaSettings.fillDarkColor,
				strokeWidth: 1,
				fill: konvaSettings.fillGreyColor
			}));
			void this.drawHiddenLines(elementGroup);
		} else {
			elementGroup.add(new Konva.Rect({
				name: 'dark active fill-hover-light',
				x: 0,
				y: 0,
				width: elementGroup.width(),
				height: elementGroup.height(),
				stroke: active ? konvaSettings.activeColor : konvaSettings.fillDarkColor,
				strokeWidth: 1,
				fill: active ? konvaSettings.activeColor : konvaSettings.fillDarkColor
			}));
		}

		return elementGroup;
	}

	protected getTooltipValues(): Partial<ITooltipLabels> {
		const layoutMeters: number = this._element.layoutMeters,
			freeSpaceOnHook = this._element.Depth - layoutMeters,
			productsGroupLength = this._element.products.map(p => p.length),
			quantitySKU = productsGroupLength.length ? productsGroupLength.reduce((a, b) => Number(a) + Number(b)) : 0;

		return {
			ONE_HOOK: Utils.getElementIndex(this._element, this._equipment) || NOT_AVAILABLE_LABEL,
			DEPTH: UnitConverter.getConvertValue(this._element.Depth, 'distance', 'inches', 3),
			LINEAR_METERS: UnitConverter.getConvertValue(layoutMeters, 'distance', 'feet', 3),
			FREE_SPACE_ON_THE_HOOK_M: UnitConverter.getConvertValue(freeSpaceOnHook, 'distance', 'inches', 3),
			QUANTITY_OF_LAID_OUT_SKU: quantitySKU
		};
	}
}

class PartitionFrontState implements IEquipmentSideState {

	getPoint(element: Element, equipment: Equipment): Point {
		let end: Point = {
			x: Utils.round(element.shapes[0].begin.x + element.shapes[0].width),
			y: Utils.round(element.shapes[0].begin.y + element.shapes[0].height),
			z: element.shapes[0].begin.z
		};

		if (element.depend.parent.is(EquipmentElements.AngleShelf)) {
			end = (element.shapes[0] as Rectangle).rotatePointByFrontAngle(end);
		}

		return {
			x: element.shapes[0].begin.x,
			y: equipment.Height - end.y
		}
	}
}

class PartitionBackState implements IEquipmentSideState {

	getPoint(element: Element, equipment: Equipment): Point {
		let end: Point = {
			x: Utils.round(element.shapes[0].begin.x + element.shapes[0].width),
			y: Utils.round(element.shapes[0].begin.y + element.shapes[0].height),
			z: element.shapes[0].begin.z
		};

		if (element.depend.parent.is(EquipmentElements.AngleShelf)) {
			end = (element.shapes[0] as Rectangle).rotatePointByFrontAngle(end);
		}

		return {
			x: equipment.Width - end.x,
			y: equipment.Height - end.y
		}
	}
}

export class PartitionElementDrawer extends ElementDrawer {

	public setState(flip: boolean = false): IElementDrawer {
		this._sideState = flip ? new PartitionBackState() : new PartitionFrontState();

		return this;
	}

	public draw(active: boolean): Konva.Group {
		const point: Point = this.convertPointToCanvas(this._sideState.getPoint(this._element, this._equipment));
		let begin: Point = {
				...this._element.shapes[0].begin,
				z: Utils.round(this._element.shapes[0].begin.z + this._element.shapes[0].depth)
			},
			end: Point = {
				x: Utils.round(this._element.shapes[0].begin.x + this._element.shapes[0].width),
				y: Utils.round(this._element.shapes[0].begin.y + this._element.shapes[0].height),
				z: this._element.shapes[0].begin.z
			};

		if (this._element.depend.parent.is(EquipmentElements.AngleShelf)) {
			begin = (this._element.shapes[0] as Rectangle).rotatePointByFrontAngle(begin);
			end = (this._element.shapes[0] as Rectangle).rotatePointByFrontAngle(end);
		}

		const elementGroup: Konva.Group = new Konva.Group({
			name: this._element.parent.type,
			x: point.x + 0.5,
			y: point.y + 0.5,
			width: (end.x - begin.x) * this._scale - 1,
			height: (end.y - begin.y) * this._scale - 1
		});

		elementGroup.add(new Konva.Rect({
			name: 'dark active',
			x: 0,
			y: 0,
			width: elementGroup.width(),
			height: elementGroup.height(),
			stroke: active ? konvaSettings.activeColor : konvaSettings.fillDarkColor,
			strokeWidth: 1,
			fill: active ? konvaSettings.activeColor : konvaSettings.fillDarkColor
		}));

		return elementGroup;
	}
}

export class ElementDrawerFactory {

	private static drawers: { [key in EquipmentElements]: typeof ElementDrawer } = {
		[EquipmentElements.BaseRectangle]: RectangleBaseElementDrawer,
		[EquipmentElements.BaseTrapeze]: TrapezeBaseElementDrawer,
		[EquipmentElements.BackWall]: BackWallElementDrawer,
		[EquipmentElements.CentralPillar]: CentralPillarElementDrawer,
		[EquipmentElements.HookWall]: HookWallElementDrawer,
		[EquipmentElements.PerforatedWall]: PerforatedWallElementDrawer,
		[EquipmentElements.SideWalls]: SideWallsElementDrawer,
		[EquipmentElements.Showcase]: ShowCaseElementDrawer,
		[EquipmentElements.Shelf]: ShelfElementDrawer,
		[EquipmentElements.AngleShelf]: AngleShelfElementDrawer,
		[EquipmentElements.HangBar]: HangBarElementDrawer,
		[EquipmentElements.Box]: BoxElementDrawer,
		[EquipmentElements.SlicedBox]: SlicedBoxElementDrawer,
		[EquipmentElements.HookPanel]: HookWallElementDrawer,
		[EquipmentElements.Hook]: HookElementDrawer,
		[EquipmentElements.Perforation]: PerforatedWallElementDrawer,
		[EquipmentElements.Hole]: ElementDrawer,
		[EquipmentElements.Partition]: PartitionElementDrawer
	}

	public static getDrawer(element: Element): ElementDrawer {
		if (ElementDrawerFactory.drawers.hasOwnProperty(element.parent.type)) {
			return new ElementDrawerFactory.drawers[element.parent.type](element);
		} else {
			throw new Error(`No such element drawer ${element.parent.type}`);
		}
	}
}